/* F1 offer preview only. Shopify's automatic discount remains authoritative. */
(() => {
  'use strict';
  if (customElements.get('f1-offer')) return;
  const elements = new Set();
  const prompts = new Set();
  let state = null;
  let receivedAt = performance.now();
  let requestNumber = 0;
  let controller;
  let refreshTimer;
  let renderFrame;

  function scheduleRender() {
    cancelAnimationFrame(renderFrame);
    renderFrame = requestAnimationFrame(() => {
      elements.forEach((element) => element.render());
      prompts.forEach((prompt) => prompt.render());
      const sliders = new Set(Array.from(elements, (element) =>
        element.closest('[data-pair-products], [data-upsell-slider]')).filter(Boolean));
      sliders.forEach((slider) => window.theme?.Flickity?.data(slider)?.resize());
    });
  }

  function acceptState(next) {
    state = next && Array.isArray(next.items) ? next : null;
    receivedAt = performance.now();
    scheduleRender();
  }

  function readState(root) {
    const script = root.querySelector('[data-f1-offer-state]');
    if (!script) throw new Error('Missing offer state');
    return JSON.parse(script.textContent);
  }

  function invalidate() {
    requestNumber += 1;
    controller?.abort();
    acceptState(null);
  }

  async function refresh() {
    invalidate();
    const version = requestNumber;
    controller = new AbortController();
    const activeController = controller;
    const timeout = setTimeout(() => activeController.abort(), 8000);
    try {
      const url = new URL(window.theme.routes.cart_url, location.origin);
      url.searchParams.set('section_id', 'f1-offer-state');
      const response = await fetch(url, {
        credentials: 'same-origin', cache: 'no-store', signal: activeController.signal
      });
      if (!response.ok) throw new Error('Offer state unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (version === requestNumber) acceptState(readState(html));
    } catch (_) {
      // Keep the original theme prices if the cart cannot be verified.
      if (version === requestNumber) acceptState(null);
    } finally {
      clearTimeout(timeout);
    }
  }

  function queueRefresh() {
    invalidate();
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refresh, 80);
  }

  function offerContext() {
    if (!state?.enabled || !state.collection || state.blocked) return null;
    const percent = Number(state.percent);
    const startsAt = Date.parse(state.startsAt);
    const endsAt = Date.parse(state.endsAt);
    const now = Number(state.serverNow) * 1000 + performance.now() - receivedAt;
    if (!Number.isFinite(now) || !Number.isFinite(startsAt) || !Number.isFinite(endsAt)
      || now < startsAt || now >= endsAt || !(percent > 0 && percent <= 100)) return null;
    const lines = state.items.filter((item) => item.eligible && Number.isInteger(item.quantity)
      && item.quantity > 0 && Number.isFinite(item.price) && item.price > 0)
      .sort((a, b) => a.price - b.price);
    const count = lines.reduce((sum, item) => sum + item.quantity, 0);
    const used = Math.floor(count / 2);
    const maxUses = Number(state.maxUses) || 0;
    if ((maxUses > 0 && used >= maxUses) || (count >= 2 && !state.applied)) return null;
    let position = 0;
    const threshold = lines.find((item) => {
      position += item.quantity;
      return position > used;
    })?.price;
    return {percent, count, threshold};
  }

  function money(cents) {
    const theme = window.theme;
    if ((state?.currency || theme.current_iso_code) === 'LKR') {
      return `LKR ${new Intl.NumberFormat('en-LK', {maximumFractionDigits: 2}).format(cents / 100)}`;
    }
    const format = theme.settings.currency_code_enable ? theme.moneyWithCurrencyFormat : theme.moneyFormat;
    return theme.formatMoney(cents, format);
  }

  function priceText(cents) {
    const text = document.createElement('span');
    text.innerHTML = money(cents);
    return text.textContent;
  }

  function entriesText(total) {
    return total === 2
      ? 'Buy both tees to get 2 entries in the draw to win an F1 replica.'
      : `Buy all ${total} F1 tees to get ${total} entries in the draw to win an F1 replica.`;
  }

  class F1OfferPrompt extends HTMLElement {
    connectedCallback() {
      prompts.add(this);
      scheduleRender();
    }

    disconnectedCallback() { prompts.delete(this); }

    render() {
      this.hidden = true;
      const context = offerContext();
      if (!context || context.count % 2 !== 1 || !window.theme?.formatMoney) return;
      const offers = Array.from(this.parentElement.querySelectorAll('f1-offer[data-mode="card"]'))
        .filter((element) => element.quote && !element.closest('[data-upsell-holder]')?.classList.contains('fade-out'));
      if (!offers.length) return;
      const lowest = Math.min(...offers.map((element) => element.quote.price));
      const varies = offers.some((element) => element.product.varies || element.quote.price !== lowest);
      const design = context.count === 1 ? 'your 2nd' : 'another';
      this.querySelector('[data-f1-prompt-title]').textContent =
        `Get ${design} F1 design ${varies ? 'from' : 'for'} ${priceText(lowest)}`;
      this.querySelector('[data-f1-prompt-body]').textContent = entriesText(context.count + 1);
      this.querySelector('[data-f1-prompt-hint]').textContent =
        context.count === 1 ? 'Choose your 2nd design below.' : 'Choose your next design below.';
      this.hidden = false;
    }
  }

  class F1Offer extends HTMLElement {
    connectedCallback() {
      try {
        this.product = JSON.parse(this.dataset.offerProduct);
      } catch (_) { return; }
      this.mode = this.dataset.mode;
      this.productForm = this.closest('product-form');
      this.form = this.productForm?.querySelector('form[data-product-form]');
      this.scope = this.productForm?.closest('[data-section-type]')
        || this.productForm?.closest('[data-quick-view-item-holder]')
        || this.productForm?.closest('[data-quick-view-inner]');
      this.card = this.closest('[data-upsell-holder]');
      this.variantChanged = false;
      elements.add(this);
      scheduleRender();
    }

    disconnectedCallback() {
      elements.delete(this);
      this.clearButtonQuotes();
    }

    clearButtonQuotes() {
      if (this.mode === 'card') {
        this.card?.querySelectorAll('[data-f1-button-suffix]').forEach((node) => node.remove());
      } else {
        this.productForm?.querySelectorAll('[data-f1-quoted]').forEach((button) => {
          button.removeAttribute('data-f1-quoted');
          button.querySelectorAll('.f1-offer__button-price').forEach((node) => node.remove());
        });
      }
    }

    variant() {
      const input = this.form?.querySelector('[name="id"]');
      const id = this.variantChanged ? this.variantId : (input?.value || this.product.selectedId);
      return this.product.variants.find((variant) => String(variant.id) === String(id));
    }

    render() {
      this.clearButtonQuotes();
      this.quote = null;
      const regular = this.querySelector('[data-f1-regular]');
      const special = this.querySelector('[data-f1-special]');
      const note = this.querySelector('[data-f1-note]');
      if (regular) regular.hidden = false;
      special.hidden = true;
      note.hidden = true;
      const context = offerContext();
      const variant = this.variant();
      if (!context || !variant?.available || !window.theme?.formatMoney) return;
      const quantity = this.mode === 'card' ? 1 : Number(this.form?.querySelector('[name="quantity"]')?.value || 1);
      const {percent, count, threshold} = context;
      const quoted = quantity === 1 && count % 2 === 1 && variant.price <= threshold;
      note.hidden = false;
      if (!quoted) {
        note.textContent = quantity !== 1
          ? 'Pair offer total calculated in cart. Each F1 tee you buy earns 1 draw entry.'
          : count === 0
            ? 'Buy 2 F1 tees for a pair offer and 2 entries in the F1 replica draw.'
            : count % 2 === 0
              ? 'Add another pair of F1 tees for another offer. Each tee you buy earns 1 draw entry.'
              : 'The pair offer applies to the lower-priced F1 tee.';
        return;
      }
      const discounted = variant.price - Math.round(variant.price * percent / 100);
      this.quote = {price: discounted};
      const was = money(variant.price);
      const now = money(discounted);
      const from = this.mode === 'card' && this.product.varies;
      if (regular) regular.hidden = true;
      special.hidden = false;
      this.querySelector('[data-f1-from]').hidden = !from;
      this.querySelector('[data-f1-was]').innerHTML = was;
      this.querySelector('[data-f1-now]').innerHTML = now;
      note.textContent = `Save ${priceText(variant.price - discounted)}. ` +
        (this.mode === 'form' ? entriesText(count + 1) : 'Offer applied in cart.');
      if (from) note.textContent += ' Price depends on size.';

      if (this.mode === 'card') {
        this.card.querySelectorAll('.product-upsell__btn-text, [data-upsell-btn-text]').forEach((label) => {
          const suffix = document.createElement('span');
          suffix.setAttribute('data-f1-button-suffix', '');
          suffix.innerHTML = ` — ${from ? 'from ' : ''}${now}`;
          label.append(suffix);
        });
      } else {
        this.productForm.querySelectorAll('[data-add-to-cart]').forEach((button) => {
          const label = button.querySelector('[data-add-to-cart-text]');
          if (!label || button.disabled || button.hasAttribute('data-upsell-btn')) return;
          const prices = document.createElement('span');
          prices.className = 'f1-offer__button-price';
          prices.innerHTML = `<s>${was}</s><span>${now}</span>`;
          label.append(prices);
          button.setAttribute('data-f1-quoted', '');
        });
      }
    }
  }

  try { acceptState(readState(document)); } catch (_) { acceptState(null); }
  customElements.define('f1-offer', F1Offer);
  customElements.define('f1-offer-prompt', F1OfferPrompt);

  document.addEventListener('theme:variant:change', (event) => {
    elements.forEach((element) => {
      if (element.mode === 'form' && element.scope === event.target) {
        element.variantChanged = true;
        element.variantId = event.detail?.variant?.id ?? null;
      }
    });
    scheduleRender();
  });
  document.addEventListener('change', (event) => {
    if (event.target.closest('product-form')) scheduleRender();
  });
  document.addEventListener('theme:cart:add', invalidate);
  document.addEventListener('theme:cart:update', invalidate);
  document.addEventListener('theme:cart:added', queueRefresh);
  document.addEventListener('theme:cart:close', queueRefresh);
  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-item-remove]')) invalidate();
  }, true);
  document.addEventListener('shopify:section:load', queueRefresh);
  window.addEventListener('pageshow', (event) => { if (event.persisted) queueRefresh(); });
  window.addEventListener('focus', queueRefresh);
  // A page left open must stop advertising a campaign when its display window ends.
  setInterval(scheduleRender, 30000);
})();
